import { ReactElement, useEffect, useState } from 'react';
import { LoadingView } from '../LoadingView/LoadingView';
import { setGlobalState, useGlobalState } from '../../state/state';
import {
  CAMPAIGN_CODE_QUERY_PARAM,
  JWT_TOKEN_STORAGE_KEY,
  MOBILE_APP_QUERY_PARAM,
  MOBILE_APP_QUERY_PARAM_STORAGE_KEY,
  MOCK_TOKEN,
  Pages,
  QUERY_PARAM_KEY,
  SESSION_EXPIRED_STATUS_CODES,
} from '../../utils/constants';
import { Cookies } from 'react-cookie';
import { clearGlobalState, generateInitialFormData, getLoggedInMember } from '../../state/helpers';
import { IPaymentInfo, AppName, MembershipLevel } from '@lib/types';
import { sources } from '../../api/sources';
import { IContent, IMemberDetails, IShortBenefits } from '../../state/model';
import { mapMemberDetailsApiResponseToState } from '../../state/apiToStateMaps';
import { IGetContentResponse, IGetPaymentInfoRequest } from '../../api/contracts';
import { useHistory, useLocation } from 'react-router-dom';
import { buildNotifications } from '../Notifications/buildNotifications';
import { navigateToMembershipBillPay } from '../../utils/navigateToMembershipBillPay';
import { isMonthlyPastDue } from '../../utils/ruleHelper';
import { UtagHelper } from '../../utils/UtagHelper';
import LogCannon from '@ace-clublabs/log-cannon';
import { createAceIdentity } from './utils/createAceIdentity';
import { getIsMobileApp, getIdentityUrl } from './utils/helpers';
import { REDIRECT_PATH, PATH_KEY } from './utils/constants';
import StatusCode from 'status-code-enum';
import { useSessionTimeout, MSS_DEFAULT_SESSION_DURATION, MSSSession, getPostLogoutRedirectUrl } from '../../hooks';
import { IAdditionalBenefits } from '@lib/types/dist/IAdditionalBenefits';

export interface MembershipDetailsProviderProps {
  children: ReactElement;
  useStubs?: boolean;
}

export const MembershipDetailsProvider = (props: MembershipDetailsProviderProps) => {
  const history = useHistory();
  const location = useLocation();
  const [, setIsMobileApp] = useGlobalState('isMobileApp');
  const [finishLoadingProps, setFinishLoadingProps] = useState({ finished: false, onFinish: () => {} });
  const [formData] = useGlobalState('formData');
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const { setSessionTimeoutParameters } = useSessionTimeout();

  // auth functions
  const setToken = (token: string) => {
    sessionStorage.setItem(JWT_TOKEN_STORAGE_KEY, token);
  };

  const onCompleteAuth = (token: string) => {
    setToken(token);
    redirect();
    setAuthenticated(true);
  };

  const authenticate = async () => {
    if (props.useStubs) {
      setAuthenticated(true);
      setToken(MOCK_TOKEN);
    } else {
      try {
        const aceIdentity = createAceIdentity(window.location.host, REDIRECT_PATH);
        await aceIdentity.ready();

        const tokens = await aceIdentity.tokens();
        const accessToken = tokens?.accessToken || '';

        if (accessToken) {
          const mssSession = new MSSSession(aceIdentity, MSS_DEFAULT_SESSION_DURATION);
          const expirationTime = await mssSession.getSessionExpirationTime();
          const postLogoutRedirectUrl = getPostLogoutRedirectUrl(Pages.TimeoutError);
          const logoutUrl = getIdentityUrl('logout', postLogoutRedirectUrl);

          setSessionTimeoutParameters({
            expiresAt: expirationTime,
            onSessionExpired: () => {
              if (isLoading) setFinishLoadingProps({ finished: true, onFinish: () => setLoading(false) });
              window.location.assign(logoutUrl);
            },
          });

          onCompleteAuth(accessToken);
        }
      } catch (error: any) {
        LogCannon.error('identity Error thrown getting token from PKCE', error);
        setFinishLoadingProps({
          finished: true,
          onFinish: () => {
            history.push(Pages.SorryPage);
            setLoading(false);
          },
        });
      }
    }
  };

  const redirect = () => {
    const savedPath = sessionStorage.getItem(PATH_KEY)!;
    const savedParams = sessionStorage.getItem(QUERY_PARAM_KEY);

    const hasSavedRedirect = (savedPath && savedPath !== Pages.HomePage) || savedParams;

    const pathAndParameters = window.location.pathname.replace(REDIRECT_PATH, '') + window.location.search;
    const isOnHomePage = pathAndParameters === Pages.HomePage || pathAndParameters === '';

    sessionStorage.removeItem(PATH_KEY);
    sessionStorage.removeItem(QUERY_PARAM_KEY);

    if (hasSavedRedirect && isOnHomePage) {
      let uri = '';

      if (savedPath) {
        uri += savedPath;
      }

      if (savedParams) {
        uri += `?${savedParams}`;
      }

      history.push(uri);
    }
  };

  const saveRedirect = () => {
    const savedPath = sessionStorage.getItem(PATH_KEY);
    const savedParams = sessionStorage.getItem(QUERY_PARAM_KEY);

    const path = window.location.pathname.replace(REDIRECT_PATH, '');
    if (!savedPath) {
      sessionStorage.setItem(PATH_KEY, path);
    }

    const params = new URLSearchParams(window.location.search).toString();
    if (!savedParams) {
      sessionStorage.setItem(QUERY_PARAM_KEY, params);
    }
  };

  // member details functions
  const saveMemberDetails = (membershipDetails: IMemberDetails, paymentInfo: IPaymentInfo) => {
    setGlobalState('memberDetails', mapMemberDetailsApiResponseToState(membershipDetails));
    const { memberCosts } = membershipDetails;
    const priceCart = memberCosts?.priceCart;
    const prospectiveCosts = memberCosts?.prospectiveCosts;
    const additionalDiscounts = memberCosts?.additionalDiscounts;
    if (!memberCosts || !priceCart || !prospectiveCosts || !additionalDiscounts) {
      throw new Error('Missing member costs');
    }

    const baseTags = UtagHelper.buildTags(membershipDetails);

    if (!formData.requestedBillPlan) {
      setGlobalState('formData', generateInitialFormData(membershipDetails));
    }
    setGlobalState('tags', baseTags);
    setGlobalState('priceCart', priceCart);
    setGlobalState('prospectiveCosts', prospectiveCosts);
    setGlobalState('additionalDiscounts', additionalDiscounts);
    setGlobalState('paymentInfo', paymentInfo);
    setGlobalState('notifications', buildNotifications(membershipDetails, paymentInfo, baseTags, history));
  };

  const processBenefits = (additionalBenefits: IAdditionalBenefits[]) => {
    additionalBenefits.forEach((additionalBenefit, idx) => {
      if (additionalBenefit == null || !additionalBenefit?.group1 || !additionalBenefit?.valuePairs) return;

      const groups = [additionalBenefit.group1, additionalBenefit.group2].filter(Boolean);
      groups.forEach((_, i) => {
        for (var j = 0; j < additionalBenefit.valuePairs!.length; j++) {
          var replaceValue = additionalBenefit.valuePairs?.[j].value.text;
          if (additionalBenefit.valuePairs?.[j].value.isLink) {
            replaceValue =
              '<a href="' +
              additionalBenefit.valuePairs[j].value.link +
              '" target="_blank" style="color: #0156A3">' +
              additionalBenefit.valuePairs[j].value.text +
              '</a>';
          }
          groups[i].benefits.forEach((_, k) => {
            groups[i].benefits[k] = groups[i].benefits[k].replaceAll(
              '{{' + additionalBenefit.valuePairs?.[j].name + '}}',
              replaceValue!
            );
          });
        }

        groups[i].benefits.forEach((_, k) => {
          const hasRegistrationSymbol = groups[i].benefits[k].includes('®');
          if (hasRegistrationSymbol) {
            groups[i].benefits[k] = groups[i].benefits[k].replaceAll(
              '®',
              '<sup style="font-size: 0.6rem; vertical-align: text-bottom">®</sup>'
            );
          }
        });
      });
    });

    return additionalBenefits;
  };

  const getMembershipInfo = async () => {
    try {
      const cookies = new Cookies();
      const campaignCode =
        new URLSearchParams(window.location.search)?.get(CAMPAIGN_CODE_QUERY_PARAM) ||
        cookies.get(CAMPAIGN_CODE_QUERY_PARAM);
      const membershipDetails = await sources.getMembershipDetails({ campaignCode });
      const request = buildPaymentInfoRequest(membershipDetails);
      const paymentInfo = await sources.getPaymentInfo(request!);
      saveMemberDetails(membershipDetails, paymentInfo);

      const getContentResponse = await sources.getContent({
        clubCode: membershipDetails.clubCode!,
        appName: AppName.MSS,
        campaignCode,
        region: membershipDetails.clubRegion!,
      });
      const shortBenefitsStateObject: IShortBenefits = mapGetContentResponseToShortBenefitsState(getContentResponse);

      const content: IContent = {
        greenBoxMessage: getContentResponse.data?.greenBoxMessage,
        shortBenefits: shortBenefitsStateObject,
        additionalBenefits: processBenefits(getContentResponse.data?.additionalBenefits || []),
      };
      setGlobalState('content', content);

      if (isMonthlyPastDue(membershipDetails)) {
        const baseTags = UtagHelper.buildTags(membershipDetails);
        await navigateToMembershipBillPay(membershipDetails, baseTags, history);
      }

      setFinishLoadingProps({ finished: true, onFinish: () => setLoading(false) });
    } catch (e: any) {
      console.error('membershipDetails.error', e);
      setFinishLoadingProps({
        finished: true,
        onFinish: () => {
          let errorPage;
          if (e?.error?.code === StatusCode.ServerErrorServiceUnavailable) {
            errorPage = Pages.MaintenancePage;
          } else {
            errorPage = SESSION_EXPIRED_STATUS_CODES.includes(e?.error?.code) ? Pages.TimeoutError : Pages.SorryPage;
          }
          history.push(errorPage);
          setLoading(false);
        },
      });
    }
  };

  useEffect(() => {
    const isErrorOrMaintenancePage = [
      Pages.SorryPage,
      Pages.TimeoutError,
      Pages.PaymentError,
      Pages.MaintenancePage,
    ].includes(location.pathname as Pages);

    if (isErrorOrMaintenancePage) {
      setFinishLoadingProps({ finished: true, onFinish: () => setLoading(false) });
      return;
    }

    if (isAuthenticated) {
      const isNavigatedFromPayments =
        window.location.pathname === `/membership/selfservice${Pages.UpdateMembershipStatusPage}`;

      if (isNavigatedFromPayments) {
        setFinishLoadingProps({ finished: true, onFinish: () => setLoading(false) });
        return;
      }

      if (window.history.state?.navigatedFrom === Pages.ConfirmationPage) {
        window.history.replaceState(null, '', window.location.href);
        clearGlobalState(setGlobalState);
      }

      getMembershipInfo();

      const mobileAppQueryParamValue = getIsMobileApp();
      if (mobileAppQueryParamValue === true) {
        sessionStorage.setItem(
          MOBILE_APP_QUERY_PARAM_STORAGE_KEY,
          `${MOBILE_APP_QUERY_PARAM}=${mobileAppQueryParamValue}`
        );
        setIsMobileApp(true);
      }
    } else {
      saveRedirect();
      authenticate();
    }
  }, [isAuthenticated]);

  return isLoading ? (
    <LoadingView
      finishProps={finishLoadingProps}
      message={'Gathering your membership information'}
      estimatedTimeInSeconds={52}
    />
  ) : (
    <>{props.children}</>
  );
};

function buildPaymentInfoRequest(membershipDetails: IMemberDetails): IGetPaymentInfoRequest {
  const { clubCode, currentBillPlan, membershipNumber } = membershipDetails;
  const loggedInMember = getLoggedInMember(membershipDetails);
  const roleCode = loggedInMember?.roleCode;

  if (!clubCode || !currentBillPlan || !membershipNumber || !loggedInMember || !roleCode) {
    throw new Error('Missing parameter required for get payment info request');
  }

  const eightDigitMembershipNumber = membershipNumber.slice(6, 14);

  return {
    clubCode,
    billPlan: currentBillPlan,
    membershipNumber: eightDigitMembershipNumber,
    roleCode,
  };
}

function mapGetContentResponseToShortBenefitsState(response: IGetContentResponse): IShortBenefits {
  const additionalBenefits = (response?.data?.additionalBenefits as IAdditionalBenefits[]) || [];

  const shortBenefitsStateObject: IShortBenefits = {
    [MembershipLevel.CLASSIC]: [],
    [MembershipLevel.PLUS]: [],
    [MembershipLevel.PREMIER]: [],
  };

  additionalBenefits.forEach(({ membershipLevel, shortBenefits }) => {
    const benefitLevel = membershipLevel as MembershipLevel;
    if (benefitLevel in shortBenefitsStateObject) {
      shortBenefitsStateObject[membershipLevel as MembershipLevel].push(...shortBenefits);
    }
  });

  return shortBenefitsStateObject;
}
